import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { TimezoneService } from '../../../core/services/timezone.service';

export interface DateRangeValue {
  start: string | null;
  end: string | null;
}

export interface DatePickerTimezoneSingleValue {
  local_value: string | null;
  utc_value: string | null;
  timezone: string;
}

export interface DatePickerTimezoneRangeValue {
  start_local: string | null;
  end_local: string | null;
  start_time_utc: string | null;
  end_time_utc: string | null;
  timezone: string;
}

export type DatePickerValue =
  | string
  | DateRangeValue
  | DatePickerTimezoneSingleValue
  | DatePickerTimezoneRangeValue
  | null;

@Component({
  selector: 'app-date-picker-input',
  standalone: false,
  templateUrl: './date-picker-input.component.html',
  styleUrl: './date-picker-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DatePickerInputComponent),
      multi: true,
    },
  ],
})
export class DatePickerInputComponent implements ControlValueAccessor, Validator, OnChanges {
  @Input() label = '';
  @Input() placeholder = 'Select date';
  @Input() helperText = '';
  @Input() errorText = '';
  @Input() mode: 'single' | 'range' = 'single';
  @Input() includeTime = false;
  @Input() minDate: string | Date | null = null;
  @Input() maxDate: string | Date | null = null;
  @Input() minTime: string | null = null;
  @Input() maxTime: string | null = null;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() required = false;
  @Input() invalid = false;
  @Input() timezoneAware = false;
  @Input() timezone: string | null = null;
  @Input() inputClass = 'form-control';

  @Output() valueChange = new EventEmitter<DatePickerValue>();

  singleValue = '';
  rangeStart = '';
  rangeEnd = '';

  private touched = false;
  private timeErrorMessage = '';
  private onChange: (value: DatePickerValue) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  constructor(private readonly timezoneService: TimezoneService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['mode'] ||
      changes['includeTime'] ||
      changes['minTime'] ||
      changes['maxTime'] ||
      changes['minDate'] ||
      changes['maxDate']
    ) {
      this.validateLocalConstraints();
      this.onValidatorChange();
    }
  }

  get inputType(): 'date' | 'datetime-local' {
    return this.includeTime ? 'datetime-local' : 'date';
  }

  get minInputValue(): string | null {
    return this.toInputDateValue(this.minDate);
  }

  get maxInputValue(): string | null {
    return this.toInputDateValue(this.maxDate);
  }

  get resolvedTimezone(): string {
    return this.timezoneService.validateTimezone(this.timezone);
  }

  get displayError(): string {
    if (this.errorText && this.invalid) {
      return this.errorText;
    }

    if (this.timeErrorMessage) {
      return this.timeErrorMessage;
    }

    if (!this.required || !this.touched) {
      return '';
    }

    if (this.mode === 'single') {
      return this.singleValue.trim() ? '' : 'Please select a date.';
    }

    return this.rangeStart.trim() && this.rangeEnd.trim()
      ? ''
      : 'Please select both start and end dates.';
  }

  writeValue(value: DatePickerValue): void {
    if (this.mode === 'range') {
      if (this.isTimezoneRangeValue(value)) {
        this.rangeStart =
          value.start_local ||
          this.timezoneService.utcIsoToLocalInput(value.start_time_utc || '', value.timezone) ||
          '';
        this.rangeEnd =
          value.end_local ||
          this.timezoneService.utcIsoToLocalInput(value.end_time_utc || '', value.timezone) ||
          '';
      } else {
        const normalized = this.toRangeValue(value);
        this.rangeStart = normalized.start || '';
        this.rangeEnd = normalized.end || '';
      }
      this.singleValue = '';
      this.validateLocalConstraints();
      return;
    }

    if (this.isTimezoneSingleValue(value)) {
      this.singleValue =
        value.local_value ||
        this.timezoneService.utcIsoToLocalInput(value.utc_value || '', value.timezone) ||
        '';
    } else {
      this.singleValue = this.toStringValue(value);
    }

    this.rangeStart = '';
    this.rangeEnd = '';
    this.validateLocalConstraints();
  }

  registerOnChange(fn: (value: DatePickerValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(): ValidationErrors | null {
    if (this.timeErrorMessage) {
      return { invalidTimeRange: true };
    }

    if (!this.required) {
      return null;
    }

    if (this.mode === 'single') {
      return this.singleValue.trim() ? null : { required: true };
    }

    return this.rangeStart.trim() && this.rangeEnd.trim() ? null : { required: true };
  }

  onSingleChange(value: string): void {
    this.singleValue = value;
    this.propagateValue();
  }

  onRangeStartChange(value: string): void {
    this.rangeStart = value;
    this.propagateValue();
  }

  onRangeEndChange(value: string): void {
    this.rangeEnd = value;
    this.propagateValue();
  }

  setToday(): void {
    const now = new Date();
    const value = this.includeTime ? this.toDatetimeLocalValue(now) : this.toDateOnlyValue(now);

    if (this.mode === 'single') {
      this.singleValue = value;
      this.propagateValue();
      return;
    }

    this.rangeStart = value;
    this.rangeEnd = value;
    this.propagateValue();
  }

  markTouched(): void {
    this.touched = true;
    this.onTouched();
  }

  private propagateValue(): void {
    this.validateLocalConstraints();

    let nextValue: DatePickerValue;

    if (this.timezoneAware && this.includeTime) {
      if (this.mode === 'single') {
        nextValue = {
          local_value: this.singleValue || null,
          utc_value: this.singleValue
            ? this.timezoneService.localInputToUtcIso(this.singleValue, this.resolvedTimezone)
            : null,
          timezone: this.resolvedTimezone,
        };
      } else {
        nextValue = {
          start_local: this.rangeStart || null,
          end_local: this.rangeEnd || null,
          start_time_utc: this.rangeStart
            ? this.timezoneService.localInputToUtcIso(this.rangeStart, this.resolvedTimezone)
            : null,
          end_time_utc: this.rangeEnd
            ? this.timezoneService.localInputToUtcIso(this.rangeEnd, this.resolvedTimezone)
            : null,
          timezone: this.resolvedTimezone,
        };
      }
    } else {
      nextValue =
        this.mode === 'single'
          ? this.singleValue || null
          : {
              start: this.rangeStart || null,
              end: this.rangeEnd || null,
            };
    }

    this.onChange(nextValue);
    this.valueChange.emit(nextValue);
    this.onValidatorChange();
  }

  private validateLocalConstraints(): void {
    this.timeErrorMessage = '';
    if (!this.includeTime) {
      return;
    }

    if (this.mode === 'single') {
      this.timeErrorMessage = this.timeValidationMessage(this.singleValue);
      return;
    }

    const startError = this.timeValidationMessage(this.rangeStart);
    if (startError) {
      this.timeErrorMessage = `Start ${startError.toLowerCase()}`;
      return;
    }

    const endError = this.timeValidationMessage(this.rangeEnd);
    if (endError) {
      this.timeErrorMessage = `End ${endError.toLowerCase()}`;
    }
  }

  private timeValidationMessage(value: string): string {
    const normalized = value.trim();
    if (!normalized) {
      return '';
    }

    const timePart = this.extractTimePart(normalized);
    if (!timePart) {
      return '';
    }

    if (this.minTime && timePart < this.minTime) {
      return `time must be after ${this.minTime}.`;
    }

    if (this.maxTime && timePart > this.maxTime) {
      return `time must be before ${this.maxTime}.`;
    }

    return '';
  }

  private extractTimePart(value: string): string {
    const index = value.indexOf('T');
    if (index >= 0) {
      return value.slice(index + 1, index + 6);
    }

    return value.length >= 5 && value.includes(':') ? value.slice(0, 5) : '';
  }

  private toInputDateValue(value: string | Date | null): string | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return this.includeTime ? this.toDatetimeLocalValue(value) : this.toDateOnlyValue(value);
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (!this.includeTime) {
      return trimmed.length >= 10 ? trimmed.slice(0, 10) : trimmed;
    }

    if (trimmed.includes('T')) {
      return trimmed.slice(0, 16);
    }

    return `${trimmed.slice(0, 10)}T00:00`;
  }

  private toDatetimeLocalValue(value: Date): string {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    const hours = `${value.getHours()}`.padStart(2, '0');
    const minutes = `${value.getMinutes()}`.padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private toDateOnlyValue(value: Date): string {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toStringValue(value: DatePickerValue): string {
    if (typeof value === 'string') {
      return value;
    }

    return '';
  }

  private toRangeValue(value: DatePickerValue): DateRangeValue {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const rangeValue = value as DateRangeValue;
      const start = typeof rangeValue.start === 'string' ? rangeValue.start : null;
      const end = typeof rangeValue.end === 'string' ? rangeValue.end : null;
      return { start, end };
    }

    return { start: null, end: null };
  }

  private isTimezoneSingleValue(value: DatePickerValue): value is DatePickerTimezoneSingleValue {
    return (
      !!value &&
      typeof value === 'object' &&
      'timezone' in value &&
      ('utc_value' in value || 'local_value' in value)
    );
  }

  private isTimezoneRangeValue(value: DatePickerValue): value is DatePickerTimezoneRangeValue {
    return (
      !!value &&
      typeof value === 'object' &&
      'timezone' in value &&
      ('start_time_utc' in value || 'end_time_utc' in value || 'start_local' in value || 'end_local' in value)
    );
  }
}
